import { useEffect, useState } from "react";
import { useTheme, type DefaultTheme } from "styled-components";

import { coarserClockStep } from "../../utils/clockTime";
import {
  formatMediaTimelineTick,
  getMediaTimelineTickIntervals,
  MEDIA_TIMELINE_POLICY,
  withMinorTicks,
} from "./mediaTimelineMath";

/** Air between neighbouring major labels, at the 16px root basis. */
const LABEL_GAP_PX = 8;
/** Enough coarser steps to cross from seconds to days; the loop stops as soon as labels fit. */
const CLOCK_WIDENING_LIMIT = 8;
/** A ruler shows a few dozen majors at most; measuring more would only cost time. */
const MAX_MEASURED_LABELS = 200;

export function resolveCanvasToken(value: string, scope?: Element): string {
  const trimmed = value.trim();
  const match = /^var\(\s*(--[^,\s)]+)(?:\s*,\s*([^)]+))?\s*\)$/.exec(trimmed);
  if (!match || typeof document === "undefined") return trimmed;

  const [, variableName, fallback] = match;
  return (
    getComputedStyle(scope ?? document.documentElement)
      .getPropertyValue(variableName)
      .trim() ||
    fallback?.trim() ||
    trimmed
  );
}

function resolveTickCanvasStyles(theme: DefaultTheme) {
  return {
    minorTick: resolveCanvasToken(theme.color.border.subtle),
    majorTick: resolveCanvasToken(theme.color.text.muted),
    label: resolveCanvasToken(theme.color.text.default),
    font: `${theme.typography.weight.semibold} ${resolveCanvasToken(
      theme.typography.size.caption,
    )} ${resolveCanvasToken(theme.typography.family.body)}`,
  };
}

export type TickCanvasStyles = ReturnType<typeof resolveTickCanvasStyles>;

/**
 * Canvas fills need concrete colors, but light/dark both live behind the same `var(--c6-*)`
 * names — the split is the `data-c6-theme` attribute, which ThemeProvider applies in an effect
 * that runs after this component renders and draws. Resolving once per `theme` therefore
 * captures the previous theme's values (or none on first mount) and would keep them for every
 * later redraw, so re-resolve when the applied attribute actually changes. Resolution still
 * happens only on theme flips, never per panned frame.
 *
 * The returned identity doubles as the draw effect's repaint key: unlike DOM text, canvas text
 * does not reflow when a web font finishes loading, so a completed font load forces a fresh
 * identity to repaint labels drawn with the fallback face.
 */
export function useTickCanvasStyles(): TickCanvasStyles {
  const theme = useTheme();
  const [styles, setStyles] = useState(() => resolveTickCanvasStyles(theme));

  useEffect(() => {
    const refresh = () => {
      const next = resolveTickCanvasStyles(theme);
      setStyles((previous) =>
        previous.minorTick === next.minorTick &&
        previous.majorTick === next.majorTick &&
        previous.label === next.label &&
        previous.font === next.font
          ? previous
          : next,
      );
    };
    refresh();

    const repaintWithLoadedFonts = () => setStyles((previous) => ({ ...previous }));
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    const observesFonts = typeof fonts?.addEventListener === "function";
    if (observesFonts) fonts.addEventListener("loadingdone", repaintWithLoadedFonts);

    const observer =
      typeof MutationObserver === "undefined" ? undefined : new MutationObserver(refresh);
    observer?.observe(document.documentElement, { attributeFilter: ["data-c6-theme"] });

    return () => {
      observer?.disconnect();
      if (observesFonts) fonts.removeEventListener("loadingdone", repaintWithLoadedFonts);
    };
  }, [theme]);

  return styles;
}

export interface DrawTimelineTicksOptions {
  widthPx: number;
  heightPx: number;
  hasDuration: boolean;
  /** The media duration: every label on the ruler takes its clock form. */
  durationSeconds: number;
  viewportStart: number;
  viewportDuration: number;
  fontScale: number;
  styles: TickCanvasStyles;
}

/** Repaint the tick ruler: minor/major ticks plus clip-aware major labels. */
export function drawTimelineTicks(
  canvas: HTMLCanvasElement,
  {
    widthPx,
    heightPx,
    hasDuration,
    durationSeconds,
    viewportStart,
    viewportDuration,
    fontScale,
    styles,
  }: DrawTimelineTicksOptions,
): void {
  const context = canvas.getContext("2d");
  if (!context) return;

  const leftGutterPx = MEDIA_TIMELINE_POLICY.leftGutterPx;
  const visibleEnd = viewportStart + viewportDuration;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(widthPx * dpr));
  canvas.height = Math.max(1, Math.round(heightPx * dpr));
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, widthPx, heightPx);
  if (!hasDuration) return;

  const usableWidth = Math.max(widthPx - leftGutterPx, 1);
  const timeToX = (seconds: number) =>
    leftGutterPx + ((seconds - viewportStart) / viewportDuration) * usableWidth;
  let { major, minor, showMinor } = getMediaTimelineTickIntervals(
    viewportDuration,
    usableWidth,
    fontScale,
  );
  // Hour and day labels are longer than `MM:SS`: widen the major step until the widest label in
  // view clears its neighbours, so long media never overlaps its own ruler.
  context.font = styles.font;
  const pixelsPerSecond = usableWidth / viewportDuration;
  const labelAt = (time: number, step: number) =>
    formatMediaTimelineTick(time, step, durationSeconds);
  for (let widenings = 0; widenings < CLOCK_WIDENING_LIMIT; widenings += 1) {
    const first = Math.max(0, Math.ceil((viewportStart - Number.EPSILON) / major) * major);
    // Index-based and bounded like the paint loop: `time += major` stalls once the step falls
    // below the resolution of a very large time.
    const count = Math.min(Math.ceil((visibleEnd - first) / major) + 1, MAX_MEASURED_LABELS);
    let widest = 0;
    for (let index = 0; index < count; index += 1) {
      const time = first + index * major;
      if (time > visibleEnd + Number.EPSILON) break;
      widest = Math.max(widest, context.measureText(labelAt(time, major)).width);
    }
    if (major * pixelsPerSecond >= widest + LABEL_GAP_PX * fontScale) break;
    ({ major, minor, showMinor } = withMinorTicks(
      coarserClockStep(major),
      pixelsPerSecond,
      fontScale,
    ));
  }

  context.strokeStyle = styles.minorTick;
  context.lineWidth = 1;
  if (showMinor) {
    const firstMinor = Math.max(0, Math.ceil((viewportStart - Number.EPSILON) / minor) * minor);
    const minorCount = Math.ceil((visibleEnd - firstMinor) / minor) + 1;
    for (let index = 0; index < minorCount; index += 1) {
      const time = firstMinor + index * minor;
      if (time > visibleEnd + Number.EPSILON) break;
      const x = timeToX(time);
      if (x < leftGutterPx || x > widthPx) continue;
      context.beginPath();
      context.moveTo(x, heightPx - 4);
      context.lineTo(x, heightPx);
      context.stroke();
    }
  }

  context.strokeStyle = styles.majorTick;
  context.fillStyle = styles.label;
  context.font = styles.font;
  const firstMajor = Math.max(0, Math.ceil((viewportStart - Number.EPSILON) / major) * major);
  const majorCount = Math.ceil((visibleEnd - firstMajor) / major) + 1;
  for (let index = 0; index < majorCount; index += 1) {
    const time = firstMajor + index * major;
    if (time > visibleEnd + Number.EPSILON) break;
    const x = timeToX(time);
    if (x < leftGutterPx || x > widthPx) continue;
    context.beginPath();
    context.moveTo(x, heightPx - 10);
    context.lineTo(x, heightPx);
    context.stroke();
    // Labels scale with the root font while the tick position does not move — measure the
    // actual label instead of a fixed edge threshold so enlarged text is never clipped.
    const label = labelAt(time, major);
    const labelHalfWidthPx = context.measureText(label).width / 2;
    context.textAlign =
      x - labelHalfWidthPx < 0 ? "left" : x + labelHalfWidthPx > widthPx ? "right" : "center";
    context.fillText(label, x, heightPx - 12);
  }
}
